/**
 * @fileoverview Advanced API Client Builder with Type Inference
 * @module @monorepo/api-client/core
 *
 * Type-safe API client builder that automatically infers types from registered clients.
 * Provides the best possible TypeScript experience with full autocomplete.
 *
 * Features:
 * - Automatic type inference from client registry
 * - Fluent builder API
 * - Type-safe client access
 * - No manual type definitions needed
 */

import { HttpClient } from './http-client';
import { ContractClient } from './contract-client';

// ============================================================================
// Types
// ============================================================================

/**
 * Client constructor type
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ClientConstructor<T extends ContractClient<any>> = new (httpClient: HttpClient) => T;

/**
 * Client registry - maps client names to their constructors
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ClientRegistry = Record<string, ClientConstructor<any>>;

/**
 * Infer client instances from registry
 * This is where the TypeScript magic happens!
 */
type InferClients<TRegistry extends ClientRegistry> = {
	[K in keyof TRegistry]: TRegistry[K] extends ClientConstructor<infer TClient> ? TClient : never;
};

/**
 * API Client configuration
 */
export interface ApiClientConfig {
	/** Base URL for API */
	readonly baseURL: string;
	/** Authentication token */
	readonly authToken?: string;
	/** Request timeout in milliseconds (default: 30000) */
	readonly timeout?: number;
	/** Number of retry attempts (default: 3) */
	readonly retryAttempts?: number;
	/** Base delay between retries in milliseconds (default: 1000) */
	readonly retryDelay?: number;
	/** Client ID for authentication */
	readonly clientId?: string;
	/** Client secret for authentication */
	readonly clientSecret?: string;
	/** Enable request/response logging (default: false) */
	readonly enableLogging?: boolean;
	/** Enable metrics collection (default: true) */
	readonly enableMetrics?: boolean;
	/** Skip session management (default: false) */
	readonly skipSession?: boolean;
	/** Default headers */
	readonly defaultHeaders?: Record<string, string>;
}

/**
 * Frontend client configuration
 */
export type FrontendClientV2Config = Omit<ApiClientConfig, 'clientId'>;

// ============================================================================
// API Client Builder
// ============================================================================

/**
 * API Client Builder
 *
 * Creates type-safe API clients with automatic type inference from registry.
 *
 * @example
 * ```typescript
 * // Define client registry
 * const clientRegistry = {
 *   users: UsersClient,
 *   products: ExampleClient,
 * } as const;
 *
 * // Create client with full type inference
 * const client = createApiClient(clientRegistry, {
 *   baseURL: 'http://localhost:3000/api',
 *   authToken: 'token',
 * });
 *
 * // TypeScript knows all methods!
 * await client.users.list({ page: 1, limit: 10 });
 * await client.products.create({ ... });
 * ```
 */
export class ApiClient<TRegistry extends ClientRegistry> {
	private readonly httpClient: HttpClient;
	private readonly clients: InferClients<TRegistry>;

	constructor(
		private readonly registry: TRegistry,
		config: ApiClientConfig,
	) {
		// Build default headers
		const defaultHeaders: Record<string, string> = {
			...config.defaultHeaders,
		};

		if (config.authToken) {
			defaultHeaders['Authorization'] = `Bearer ${config.authToken}`;
		}

		// Create HTTP client
		this.httpClient = new HttpClient({
			baseURL: config.baseURL,
			timeout: config.timeout ?? 30000,
			retryAttempts: config.retryAttempts ?? 3,
			retryDelay: config.retryDelay ?? 1000,
			clientId: config.clientId ?? 'api-client',
			clientSecret: config.clientSecret ?? '',
			enableLogging: config.enableLogging ?? false,
			enableMetrics: config.enableMetrics ?? true,
			skipSession: config.skipSession ?? false,
			defaultHeaders,
		});

		// Initialize all clients from registry
		this.clients = this.initializeClients();
	}

	/**
	 * Initialize all clients from registry
	 */
	private initializeClients(): InferClients<TRegistry> {
		const clients = {} as InferClients<TRegistry>;

		for (const [name, ClientClass] of Object.entries(this.registry)) {
			clients[name as keyof TRegistry] = new ClientClass(this.httpClient) as InferClients<TRegistry>[keyof TRegistry];
		}

		return clients;
	}

	/**
	 * Get client by name (fully typed!)
	 */
	get<K extends keyof TRegistry>(name: K): InferClients<TRegistry>[K] {
		return this.clients[name];
	}

	/**
	 * Access all clients directly
	 */
	get all(): InferClients<TRegistry> {
		return this.clients;
	}

	/**
	 * Get HTTP client instance
	 */
	getHttpClient(): HttpClient {
		return this.httpClient;
	}

	/**
	 * Get API metrics
	 */
	getMetrics() {
		return this.httpClient.getMetrics();
	}
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create API client with automatic type inference
 *
 * @example
 * ```typescript
 * const client = createApiClient({
 *   users: UsersClient,
 *   products: ExampleClient,
 * } as const, {
 *   baseURL: 'http://localhost:3000/api',
 * });
 *
 * // Fully typed!
 * await client.all.users.list();
 * await client.get('products').create({ ... });
 * ```
 */
export function createApiClient<TRegistry extends ClientRegistry>(
	registry: TRegistry,
	config: ApiClientConfig,
): ApiClient<TRegistry> {
	return new ApiClient(registry, config);
}

// ============================================================================
// Proxy-based Client (Alternative Approach)
// ============================================================================

/**
 * Create API client with proxy-based access
 * Provides direct property access to clients
 *
 * @example
 * ```typescript
 * const client = createProxyApiClient({
 *   users: UsersClient,
 *   products: ExampleClient,
 * } as const, {
 *   baseURL: 'http://localhost:3000/api',
 * });
 *
 * // Direct access (no .all needed)
 * await client.users.list();
 * await client.products.create({ ... });
 * ```
 */
export function createProxyApiClient<TRegistry extends ClientRegistry>(
	registry: TRegistry,
	config: ApiClientConfig,
): InferClients<TRegistry> & { $http: HttpClient; $metrics: () => unknown } {
	const apiClient = new ApiClient(registry, config);

	// Create proxy for direct access
	return new Proxy(apiClient.all, {
		get(target, prop: string) {
			// Special properties
			if (prop === '$http') return apiClient.getHttpClient();
			if (prop === '$metrics') return () => apiClient.getMetrics();

			// Client access
			return target[prop as keyof typeof target];
		},
	}) as InferClients<TRegistry> & { $http: HttpClient; $metrics: () => unknown };
}
